import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from "luxon";

import { SuccessfulLoginResponse } from '../../types/Auth/SuccessfulLoginResponse';
import { RefreshResponse } from "../../types/Auth/RefreshResponse";
import { UserDto } from "../../DTOs/User/UserDto";
import { RoleConfig } from "../../DTOs/Settings/RoleConfigDto";

export interface AuthState {
  isAuthenticated: boolean;
  tokenType: string | null;
  accessToken: string | null;
  expiresIn: number | null;
  expiresAt: number | null;
  refreshToken: string | null;
  user: null | UserDto;
  roleConfig: RoleConfig | null;
  isAuthLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  tokenType: null,
  accessToken: null,
  expiresIn: null,
  expiresAt: DateTime.now().toMillis(),
  refreshToken: null,
  user: null,
  roleConfig: null,
  isAuthLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (state, action: PayloadAction<SuccessfulLoginResponse>) => {
      state.isAuthenticated = true;
      state.user = null;
      state.tokenType = action.payload.tokenType;
      state.accessToken = action.payload.accessToken;
      state.expiresIn = action.payload.expiresIn;
      state.expiresAt = DateTime.now().toMillis() + action.payload.expiresIn * 1000;
      state.refreshToken = action.payload.refreshToken;
      state.error = null;
      state.isAuthLoading = true;
    },
    userInfoSuccess: (state, action: PayloadAction<UserDto>) => {
      state.user = action.payload;
      // Note: isAuthLoading stays true here — roleConfigSuccess will clear it
      state.error = null;
    },
    roleConfigSuccess: (state, action: PayloadAction<RoleConfig>) => {
      state.roleConfig = action.payload;
      state.isAuthLoading = false;
      state.error = null;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.user = null;
      state.roleConfig = null;
      state.error = action.payload;
      state.isAuthLoading = false;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.roleConfig = null;
      state.error = null;
      state.isAuthLoading = false;
    },
    refreshSuccess: (state, action: PayloadAction<RefreshResponse>) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.expiresIn = action.payload.expiresIn;
      state.expiresAt = DateTime.now().toMillis() + action.payload.expiresIn * 1000;
      state.error = null;
      state.isAuthLoading = false;
    }
  },
});

export const { loginSuccess, userInfoSuccess, roleConfigSuccess, loginFailure, logout, refreshSuccess } = authSlice.actions;

export default authSlice.reducer;